import type { PluginName } from '../types';
import "./froala-plugins.d.ts"

// Plugin import map for dynamic loading
const PLUGIN_MAP: Record<PluginName, () => Promise<unknown>> = {
  align: () => import('froala-editor/js/plugins/align.min'),
  bold: () => Promise.resolve(), // Bold is built-in
  colors: () => import('froala-editor/js/plugins/colors.min'),
  emoticons: () => import('froala-editor/js/plugins/emoticons.min'),
  entities: () => import('froala-editor/js/plugins/entities.min'),
  font_size: () => import('froala-editor/js/plugins/font_size.min'),
  help: () => import('froala-editor/js/plugins/help.min'),
  image: () => import('froala-editor/js/plugins/image.min'),
  inline_class: () => import('froala-editor/js/plugins/inline_class.min'),
  line_breaker: () => import('froala-editor/js/plugins/line_breaker.min'),
  line_height: () => import('froala-editor/js/plugins/line_height.min'),
  link: () => import('froala-editor/js/plugins/link.min'),
  lists: () => import('froala-editor/js/plugins/lists.min'),
  paragraph_format: () => import('froala-editor/js/plugins/paragraph_format.min'),
  quote: () => import('froala-editor/js/plugins/quote.min'),
  special_characters: () => import('froala-editor/js/plugins/special_characters.min'),
  table: () => import('froala-editor/js/plugins/table.min'),
  url: () => import('froala-editor/js/plugins/url.min'),
  word_paste: () => import('froala-editor/js/plugins/word_paste.min'),
};

/**
 * Dynamically loads the specified Froala plugins
 * @param plugins Array of plugin names to load
 * @returns Promise that resolves when all plugins are loaded
 */
export async function loadPlugins(plugins: PluginName[]): Promise<void> {
  if (!plugins || plugins.length === 0) {
    return;
  }

  // Filter out unknown plugins and get their import functions
  const importPromises = plugins
    .filter((plugin) => PLUGIN_MAP[plugin])
    .map((plugin) => PLUGIN_MAP[plugin]());

  try {
    await Promise.all(importPromises);
  } catch {
    // Failed plugins are simply unavailable in the toolbar.
  }
}

/**
 * Default plugin set for basic rich text editing
 */
export const DEFAULT_PLUGINS: PluginName[] = [
  'table',
  'align',
  'bold',
  'link',
  'lists',
  'quote',
  'paragraph_format',
];

/**
 * Extended plugin set with more formatting options
 */
export const EXTENDED_PLUGINS: PluginName[] = [
  ...DEFAULT_PLUGINS,
  'colors',
  'font_size',
  'line_height',
  'inline_class',
  'image',
  'special_characters',
  'word_paste',
];

/**
 * Full plugin set with all available features
 */
export const FULL_PLUGINS: PluginName[] = [
  ...EXTENDED_PLUGINS,
  'emoticons',
  'entities',
  'help',
  'line_breaker',
  'url',
];
